import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  SimpleButton,
  LoadingButton,
  Select,
  TextArea,
  FormDialog,
  Spinner,
  useStateContext,
} from "@veda/dashboard-ui";
import { BsPlusLg, BsCheckCircle, BsGlobe } from "react-icons/bs";
import { usePppApi } from "../services/pppApi";
import { useAuthUser } from "../hooks/useAuthUser";

const STATO_CFG = {
  bozza:      { cls: "bg-gray-100 text-gray-600" },
  approvato:  { cls: "bg-blue-100 text-blue-700" },
  pubblicato: { cls: "bg-green-100 text-green-700" },
};

function StatoBadge({ stato }) {
  const cfg = STATO_CFG[stato] ?? { cls: "" };
  const labels = { bozza: "Bozza", approvato: "Approvato", pubblicato: "Pubblicato" };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${cfg.cls}`}>
      {labels[stato] ?? stato}
    </span>
  );
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("it-IT");
}

export default function VerbaleValutazioneList({ procedimentoId }) {
  const navigate = useNavigate();
  const { addToast, setOpenForm } = useStateContext();
  const api = usePppApi();
  const { can } = useAuthUser();
  const canCrea = can('ppp.verbale_valutazione.create');

  const [verbali, setVerbali] = useState([]);
  const [schemi, setSchemi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);

  const [form, setForm] = useState({
    schemaValutazioneId: "",
    numeroVerbale: "",
    dataVerbale: "",
    motivazioneSelezione: "",
    noteCommissione: "",
  });

  const load = useCallback(() => {
    setLoading(true);
    api.listSchemiValutazione(procedimentoId, {
      onSuccess: (data) => setSchemi(data),
    });
    api.listVerbaliValutazione(procedimentoId, {
      onSuccess: (data) => { setVerbali(data); setLoading(false); },
      onFormError: () => setLoading(false),
    });
  }, [procedimentoId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  function handleCrea() {
    if (!form.schemaValutazioneId) return;
    setSalvando(true);
    api.creaVerbaleValutazione(procedimentoId, {
      schemaValutazioneId: parseInt(form.schemaValutazioneId),
      numeroVerbale:       form.numeroVerbale || undefined,
      dataVerbale:         form.dataVerbale || undefined,
      motivazioneSelezione:form.motivazioneSelezione || undefined,
      noteCommissione:     form.noteCommissione || undefined,
    }, {
      onSuccess: (data) => {
        setSalvando(false);
        setOpenForm(false);
        addToast({ title: "Verbale creato", type: "success" });
        navigate(`/ppp/${procedimentoId}/verbali-valutazione/${data.id}`);
      },
      onError: () => {
        setSalvando(false);
        addToast({ title: "Errore durante la creazione", type: "error" });
      },
    });
  }

  const schemiOptions = [
    { value: "", label: "Seleziona schema…" },
    ...schemi.map((s) => ({ value: String(s.id), label: s.titolo })),
  ];

  if (loading) return <Spinner />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Verbali di valutazione comparativa finale (art. 193, co. 5-6 D.lgs 36/2023)
        </p>
        {canCrea && (
          <SimpleButton
            text={<><BsPlusLg className="inline mr-1" />Nuovo verbale</>}
            customFunc={() => setOpenForm(true)}
          />
        )}
      </div>

      {verbali.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
          <p className="text-gray-400 text-sm">Nessun verbale di valutazione</p>
          <p className="text-gray-400 text-xs mt-1">
            Crea il verbale dopo aver inserito i punteggi nella griglia comparativa
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {verbali.map((verbale) => (
            <div
              key={verbale.id}
              className="flex items-center gap-4 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-blue-300 cursor-pointer transition-colors"
              onClick={() => navigate(`/ppp/${procedimentoId}/verbali-valutazione/${verbale.id}`)}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 dark:text-white">
                    {verbale.numeroVerbale ?? `Verbale #${verbale.id}`}
                  </span>
                  <StatoBadge stato={verbale.stato} />
                  {verbale.stato === "approvato" && <BsCheckCircle className="text-blue-500" size={14} />}
                  {verbale.stato === "pubblicato" && <BsGlobe className="text-green-500" size={14} />}
                </div>
                <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                  {verbale.schemaTitolo && <span>Schema: {verbale.schemaTitolo}</span>}
                  {verbale.propostaSelezionataTitolo && (
                    <span className="text-green-600 font-medium">
                      Selezionata: {verbale.propostaSelezionataTitolo}
                    </span>
                  )}
                  {verbale.dataVerbale && <span>Data: {formatDate(verbale.dataVerbale)}</span>}
                  {verbale.invitoModificheInviato && (
                    <span className="text-blue-600">Invito modifiche inviato</span>
                  )}
                  {verbale.dataPubblicazioneEsito && (
                    <span>Pubblicato: {formatDate(verbale.dataPubblicazioneEsito)}</span>
                  )}
                </div>
              </div>
              <span className="text-gray-400 text-sm">›</span>
            </div>
          ))}
        </div>
      )}

      <FormDialog title="Nuovo verbale di valutazione comparativa" size="w-[580px]">
        <div className="space-y-3">
          <Select
            label="Schema di valutazione"
            options={schemiOptions}
            value={form.schemaValutazioneId}
            onChange={(e) => setForm((f) => ({ ...f, schemaValutazioneId: e.target.value }))}
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Numero verbale</label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="VV-2026-01"
                value={form.numeroVerbale}
                onChange={(e) => setForm((f) => ({ ...f, numeroVerbale: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Data verbale</label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={form.dataVerbale}
                onChange={(e) => setForm((f) => ({ ...f, dataVerbale: e.target.value }))}
              />
            </div>
          </div>
          <TextArea
            label="Note commissione"
            value={form.noteCommissione}
            onChange={(e) => setForm((f) => ({ ...f, noteCommissione: e.target.value }))}
            rows={3}
            placeholder="Seduta regolarmente tenuta..."
          />
          <div className="flex justify-end gap-2 pt-2">
            <SimpleButton customFunc={() => setOpenForm(false)} text="Annulla" />
            <LoadingButton
              isLoading={salvando}
              customFunc={handleCrea}
              text="Crea verbale"
              disabled={!form.schemaValutazioneId}
            />
          </div>
        </div>
      </FormDialog>
    </div>
  );
}
